import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, CloudOff, Loader2, LogIn, RefreshCw, XCircle } from "lucide-react";
import ProfileFavoritesPanel from "../components/profile/ProfileFavoritesPanel";
import ProfileIdentityRail from "../components/profile/ProfileIdentityRail";
import ProfileInvitePanel from "../components/profile/ProfileInvitePanel";
import ProfilePersonalPanel from "../components/profile/ProfilePersonalPanel";
import ProfileSettingsPanel from "../components/profile/ProfileSettingsPanel";
import { selectAppUser } from "../redux/appAuthSlice";
import { fetchDashboardProfile } from "../redux/dashboardSlice";
import { PROFILE_SECTIONS } from "../utils/profileHelpers";
import "../profile.css";

const Profile = () => {
  const dispatch = useDispatch();
  const authUser = useSelector(selectAppUser);
  const userId = authUser?._id;
  const { profile, profileStatus, profileError } = useSelector((s) => s.dashboard);

  const [activeSection, setActiveSection] = useState(PROFILE_SECTIONS[0].id);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    await dispatch(fetchDashboardProfile(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const onNotify = (type, title, detail) => setToast({ type, title, detail });

  const onRefresh = () => load();

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const node = document.getElementById(sectionId);
    node?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!userId) {
    return (
      <div className="prof-page">
        <div className="prof-state">
          <LogIn size={32} />
          <h1>Sign in to view your profile</h1>
          <Link to="/app/login" className="prof-primary-btn">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  if (profileStatus === "loading" && !profile) {
    return (
      <div className="prof-page">
        <div className="prof-state">
          <Loader2 size={22} className="animate-spin" />
          <p>Loading profile…</p>
        </div>
      </div>
    );
  }

  if (profileStatus === "failed" && !profile) {
    return (
      <div className="prof-page">
        <div className="prof-state">
          <CloudOff size={32} />
          <p>{String(profileError || "Could not load profile.")}</p>
          <button type="button" className="prof-primary-btn" onClick={onRefresh}>
            <RefreshCw size={13} />
            Try again
          </button>
        </div>
      </div>
    );
  }

  const user = profile || authUser;

  return (
    <div className="prof-page">
      <div className="prof-desk">
        <header className="prof-head">
          <div className="prof-head-row">
            <span className="prof-eyebrow">Profile</span>
          </div>
        </header>

        <div className="prof-workspace">
          <ProfileIdentityRail
            user={user}
            userId={userId}
            activeSection={activeSection}
            onSectionSelect={scrollToSection}
            onNotify={onNotify}
          />

          <div className="prof-content">
            <ProfilePersonalPanel user={user} userId={userId} onNotify={onNotify} />
            <ProfileFavoritesPanel user={user} userId={userId} onNotify={onNotify} />
            <ProfileInvitePanel user={user} userId={userId} onNotify={onNotify} />
            <ProfileSettingsPanel userId={userId} onNotify={onNotify} />
          </div>
        </div>
      </div>

      {toast ? (
        <div className={`prof-toast prof-toast-${toast.type}`} role="status">
          {toast.type === "success" ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
          <div>
            <strong>{toast.title}</strong>
            {toast.detail ? <span>{toast.detail}</span> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Profile;
